import React, { useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Shield } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../../config/api';
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function AdminProfile({ user, onLogout, onUpdateUser }) {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    mobile_number: user.mobile_number || '',
    profile_photo: user.profile_photo || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/users/profile`, formData, { headers: getAuthHeaders() });
      toast.success('Profile updated successfully!');
      onUpdateUser(res.data);
      localStorage.setItem('lms_user', JSON.stringify(res.data));
      setEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout} currentPage="admin-profile">
      <div className="p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#d86a2e' }}>🛡️ Admin Profile</h1>
          <p className="text-lg text-gray-600">Manage your administrator account</p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center space-x-6 mb-8">
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #ff9d5c, #ff7f3e)' }}
              >
                <Shield className="w-12 h-12 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ color: '#d86a2e' }}>
                  {user.first_name} {user.last_name}
                </h2>
                <p className="text-gray-600">{user.email}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <Shield className="w-4 h-4" style={{ color: '#d86a2e' }} />
                  <p className="text-sm font-semibold" style={{ color: '#d86a2e' }}>Administrator</p>
                </div>
              </div>
            </div>

            {editing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">First Name</label>
                    <Input
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2">Last Name</label>
                    <Input
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Mobile Number</label>
                  <Input
                    value={formData.mobile_number}
                    onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })}
                  />
                </div>
                <div className="flex space-x-3">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    style={{ background: '#ff9d5c' }}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        first_name: user.first_name || '',
                        last_name: user.last_name || '',
                        mobile_number: user.mobile_number || '',
                        profile_photo: user.profile_photo || ''
                      });
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                    <p className="text-lg">{user.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-600 mb-1">Mobile Number</label>
                    <p className="text-lg">{user.mobile_number || 'Not provided'}</p>
                  </div>
                </div>
                <Button
                  onClick={() => setEditing(true)}
                  style={{ background: '#ff9d5c' }}
                >
                  Edit Profile
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg" style={{ background: 'linear-gradient(135deg, #FFF0E6 0%, #FFE4CC 100%)' }}>
          <CardContent className="p-6">
            <h3 className="text-xl font-bold mb-4" style={{ color: '#d86a2e' }}>🛡️ Administrator Privileges</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• Full access to all system features and data</li>
              <li>• User management and bulk operations</li>
              <li>• Course and content moderation</li>
              <li>• Activity monitoring and audit logs</li>
              <li>• System configuration and settings</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}